import React, {ReactNode, useState} from 'react';
import classNames from 'classnames';
import {Link} from 'react-router-dom';
import type {LucideIcon} from 'lucide-react';
import {EllipsisIcon} from 'lucide-react';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from 'reactstrap';
import Icon from '../shared/Icon.tsx';

export type MenuItem =
    | {kind: 'divider'}
    | {
          kind?: 'item';
          label: string;
          icon?: LucideIcon;
          shortcut?: string;
          href?: string;
          onSelect?: () => void;
          // Destructive items go last, after a divider, with a red icon.
          destructive?: boolean;
          disabled?: boolean;
      };

interface MenuProps {
    // What the menu is for, for screen readers: "Project actions".
    label: string;
    items: MenuItem[];
    // The button that opens it; an ellipsis icon button by default.
    trigger?: ReactNode;
    align?: 'start' | 'end';
    size?: 'sm' | 'md';
    className?: string;
}

/*
 * Every item has an icon and a label; the hovered item gets a grey 200
 * fill; shortcuts sit on the right in grey. An ellipsis (…) on a label means
 * a confirmation follows.
 */
const Menu: React.FC<MenuProps> = ({
    label,
    items,
    trigger,
    align = 'end',
    size = 'md',
    className
}) => {
    const [open, setOpen] = useState(false);

    return (
        <Dropdown
            isOpen={open}
            toggle={() => setOpen(o => !o)}
            className={classNames('mx-menu-dropdown', className)}
        >
            <DropdownToggle
                tag="button"
                type="button"
                aria-label={label}
                className={classNames('btn btn-tertiary btn-icon-only', {
                    'btn-sm': size === 'sm'
                })}
            >
                {trigger ?? <Icon icon={EllipsisIcon} size="s" />}
            </DropdownToggle>
            <DropdownMenu end={align === 'end'} className="mx-menu">
                {items.map((item, index) =>
                    item.kind === 'divider' ? (
                        <DropdownItem key={index} divider />
                    ) : (
                        <DropdownItem
                            key={index}
                            tag={item.href ? Link : 'button'}
                            to={item.href}
                            type={item.href ? undefined : 'button'}
                            disabled={item.disabled}
                            onClick={item.onSelect}
                            className={classNames('mx-menu-item', {
                                'mx-menu-item-destructive': item.destructive
                            })}
                        >
                            {item.icon && <Icon icon={item.icon} size={18} />}
                            <span className="mx-menu-item-label">{item.label}</span>
                            {item.shortcut && (
                                <kbd className="mx-menu-shortcut">
                                    {item.shortcut}
                                </kbd>
                            )}
                        </DropdownItem>
                    )
                )}
            </DropdownMenu>
        </Dropdown>
    );
};

export default Menu;
